/**
 * A bottom sheet.
 *
 * Modals on a phone open in the middle of the screen, which puts every control
 * a stretch away from the thumb and the keyboard over the top of half of them.
 * A sheet rises from the bottom edge, so the primary button lands where the
 * hand already is.
 *
 * 92dvh, not 92vh — see .mobile-shell in index.css for why the distinction
 * matters on iOS. The sheet scrolls inside itself so a keyboard never traps a
 * field off-screen.
 */

import { useEffect, type ReactNode } from "react";

export function Sheet({
  title, onClose, children, footer, closeLabel = "Cancel",
}: {
  title: string;
  /** "Done" for a sheet whose edits apply as you make them. */
  closeLabel?: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
}) {
  // The page behind must not scroll under the sheet: on iOS that steals the
  // drag and leaves you scrolling the list you meant to stay put.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  return (
    <div
      // Above everything, the demo badge (z-index 200) included — a sheet is
      // modal, and a strip of chrome sitting across its duration chips is not a
      // small problem.
      style={{ position: "fixed", inset: 0, zIndex: 300, display: "flex", flexDirection: "column", justifyContent: "flex-end" }}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <div style={{ position: "absolute", inset: 0, background: "var(--scrim)" }} />
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          position: "relative", width: "100%", maxHeight: "92dvh",
          display: "flex", flexDirection: "column",
          background: "var(--raised)", borderRadius: "20px 20px 0 0",
          borderTop: "1px solid var(--overlayLine)",
          boxShadow: "0 -8px 40px rgba(0,0,0,0.25)",
        }}
      >
        <div style={{ display: "flex", justifyContent: "center", padding: "10px 0 2px", flex: "none" }}>
          <span style={{ width: 40, height: 4, borderRadius: 999, background: "var(--line2)" }} />
        </div>

        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "6px 16px 10px", flex: "none",
        }}>
          <span style={{ fontSize: 17, fontWeight: 600, color: "var(--ink)" }}>{title}</span>
          <button
            onClick={onClose}
            style={{
              minHeight: 44, padding: "0 6px", border: "none", background: "none",
              color: "var(--muted)", fontSize: 15, cursor: "pointer",
            }}
          >{closeLabel}</button>
        </div>

        <div style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: "0 16px" }}>
          {children}
        </div>

        {footer && (
          <div style={{
            flex: "none", padding: "12px 16px calc(12px + var(--safe-b))",
            borderTop: "1px solid var(--tint2)", background: "var(--surface)",
          }}>{footer}</div>
        )}
      </div>
    </div>
  );
}
